import {
  FottServiceRequestStatus,
  type BypassServiceDocument,
  type CreateBypassServiceRequestPayload,
  type CreateGantryCraneServiceRequestPayload,
  type CreateNitrogenSupplyServiceRequestPayload,
  type CreatePiggingServiceRequestPayload,
  type TerminalServiceDocument,
} from "@services/terminal-services/types";
import { toMinute } from "@utils/date-time";
import { FOTT_SERVICE_REQUEST_FIELDS as F, OTHER_COMPANY_VALUE } from "./constants";
import type { FottServiceRequestFormData } from "./schema";

const toIsoDateTime = (value: unknown) => toMinute(value)?.toISOString() ?? "";
const toOptionalNumber = (value: string) => {
  const trimmedValue = value.trim();

  return trimmedValue ? Number(trimmedValue) : undefined;
};

const resolveCompanySelection = ({ value, otherValue }: { value: string; otherValue: string }) => {
  const trimmedValue = value.trim();
  const trimmedOtherValue = otherValue.trim();

  if (trimmedValue === OTHER_COMPANY_VALUE) {
    return {
      companyCode: OTHER_COMPANY_VALUE,
      otherName: trimmedOtherValue,
    };
  }

  return {
    companyCode: trimmedValue,
    otherName: null,
  };
};

export const mapGantryCraneFormToPayload = ({
  values,
  operatorLicenseDocument,
  attachmentDocuments,
}: {
  values: FottServiceRequestFormData;
  operatorLicenseDocument: TerminalServiceDocument | null;
  attachmentDocuments: TerminalServiceDocument[] | null;
}): CreateGantryCraneServiceRequestPayload => {
  const onBehalfOfCompany = resolveCompanySelection({
    value: values[F.ON_BEHALF_OF_COMPANY],
    otherValue: values[F.ON_BEHALF_OF_COMPANY_OTHER],
  });

  const chargeableParty = values[F.CHARGEABLE_PARTY_SAME_AS_ON_BEHALF]
    ? onBehalfOfCompany
    : resolveCompanySelection({
        value: values[F.CHARGEABLE_PARTY],
        otherValue: values[F.CHARGEABLE_PARTY_OTHER],
      });

  return {
    bookingFrom: toIsoDateTime(values[F.BOOKING_FROM]),
    bookingTo: toIsoDateTime(values[F.BOOKING_TO]),
    matrixManifold: values[
      F.MATRIX_MANIFOLD
    ] as CreateGantryCraneServiceRequestPayload["matrixManifold"],
    onBehalfCompanyCode: onBehalfOfCompany.companyCode,
    onBehalfCompanyOther: onBehalfOfCompany.otherName ?? "",
    chargeablePartyCompanyCode: chargeableParty.companyCode,
    chargeablePartySameAsOnBehalf: values[F.CHARGEABLE_PARTY_SAME_AS_ON_BEHALF],
    chargeablePartyOther: chargeableParty.otherName ?? "",
    operatorNeeded: values[F.OPERATOR_NEEDED],
    usageReason: values[F.USAGE_REASON].trim(),
    remarks: values[F.REMARKS].trim(),
    operatorLicenseDocument,
    attachments: attachmentDocuments,
  };
};

export const mapNitrogenSupplyFormToPayload = ({
  values,
  attachmentDocuments,
}: {
  values: FottServiceRequestFormData;
  attachmentDocuments: TerminalServiceDocument[] | null;
}): CreateNitrogenSupplyServiceRequestPayload => {
  const onBehalfOfCompany = resolveCompanySelection({
    value: values[F.ON_BEHALF_OF_COMPANY],
    otherValue: values[F.ON_BEHALF_OF_COMPANY_OTHER],
  });

  const chargeableParty = values[F.CHARGEABLE_PARTY_SAME_AS_ON_BEHALF]
    ? onBehalfOfCompany
    : resolveCompanySelection({
        value: values[F.CHARGEABLE_PARTY],
        otherValue: values[F.CHARGEABLE_PARTY_OTHER],
      });

  return {
    startTime: toIsoDateTime(values[F.START_TIME]),
    usageHours: toOptionalNumber(values[F.USAGE_HOURS]),
    onBehalfCompanyCode: onBehalfOfCompany.companyCode,
    onBehalfCompanyOther: onBehalfOfCompany.otherName,
    chargeablePartyCompanyCode: chargeableParty.companyCode,
    chargeablePartyOther: chargeableParty.otherName,
    chargeablePartySameAsOnBehalf: values[F.CHARGEABLE_PARTY_SAME_AS_ON_BEHALF],
    customerJlSlotSize: values[F.CUSTOMER_JL_SLOT_SIZE].trim(),
    fottDockLineNoSize: values[F.FOTT_DOCK_LINE_NO_SIZE].trim(),
    usageReason: values[F.USAGE_REASON].trim() || undefined,
    remarks: values[F.REMARKS].trim() || undefined,
    attachments: attachmentDocuments,
    status: FottServiceRequestStatus.PENDING_APPROVAL,
  };
};

export const mapPiggingServiceFormToPayload = ({
  values,
  attachmentDocuments,
}: {
  values: FottServiceRequestFormData;
  attachmentDocuments: TerminalServiceDocument[] | null;
}): CreatePiggingServiceRequestPayload => {
  const onBehalfOfCompany = resolveCompanySelection({
    value: values[F.ON_BEHALF_OF_COMPANY],
    otherValue: values[F.ON_BEHALF_OF_COMPANY_OTHER],
  });

  const chargeableParty = values[F.CHARGEABLE_PARTY_SAME_AS_ON_BEHALF]
    ? onBehalfOfCompany
    : resolveCompanySelection({
        value: values[F.CHARGEABLE_PARTY],
        otherValue: values[F.CHARGEABLE_PARTY_OTHER],
      });

  return {
    startingFrom: toIsoDateTime(values[F.STARTING_FROM]),
    onBehalfCompanyCode: onBehalfOfCompany.companyCode,
    onBehalfCompanyOther: onBehalfOfCompany.otherName ?? "",
    chargeablePartyCompanyCode: chargeableParty.companyCode,
    chargeablePartyOther: chargeableParty.otherName ?? "",
    customerJlNo: values[F.CUSTOMER_JL_NO].trim(),
    customerTankNo: values[F.CUSTOMER_TANK_NO].trim(),
    chargeablePartySameAsOnBehalf: values[F.CHARGEABLE_PARTY_SAME_AS_ON_BEHALF],
    fottDockLine: values[F.FOTT_DOCK_LINE].trim(),
    productType: values[F.PRODUCT_TYPE] as CreatePiggingServiceRequestPayload["productType"],
    usageReason: values[F.USAGE_REASON].trim(),
    remarks: values[F.REMARKS].trim(),
    attachments: attachmentDocuments,
  };
};

export const mapBypassServiceFormToPayload = ({
  values,
  attachments,
}: {
  values: FottServiceRequestFormData;
  attachments: BypassServiceDocument[] | null;
}): CreateBypassServiceRequestPayload => {
  return {
    sourceDateTime: toIsoDateTime(values[F.DATE_TIME]),
    vesselImo: toOptionalNumber(values[F.VESSEL_IMO]),
    vesselName: values[F.VESSEL_NAME],
    customerTerminal: values[F.TERMINAL].trim(),
    location: values[F.LOCATION].trim(),
    operationType: values[F.OPERATION_TYPE].trim(),
    dockLine: values[F.DOCK_LINE].trim(),
    movNumber: values[F.MOV_NUMBER].trim(),
    plta: values[F.PLTA]?.trim() || undefined,
    pldaNumber: values[F.PLDA_NUMBER]?.trim() || undefined,
    reasonForBypass: values[F.REASON_FOR_BYPASS].trim(),
    expectedClosureDate: toIsoDateTime(values[F.EXPECTED_TIME_OF_CLOSURE]),
    physicalVerificationOfValveTag: Boolean(values[F.PHYSICAL_VERIFICATION_OF_VALVE_TAG]),
    collectionOfValveKeyFromFottCcr: Boolean(values[F.COLLECTION_OF_VALVE_KEY]),
    removalOfPadlockWitnessedByFottOperator: Boolean(values[F.REMOVAL_OF_PADLOCK]),
    crossCheckCommunication: Boolean(values[F.CROSS_CHECK_COMMUNICATION]),
    confirmationOfInstructionsWithCustomerCcr: Boolean(
      values[F.CONFIRMATION_OF_INSTRUCTIONS_CUSTOMER],
    ),
    confirmationOfInstructionsWithFottCcr: Boolean(values[F.CONFIRMATION_OF_INSTRUCTIONS_FOTT]),
    attachments: attachments,
    remarks: values[F.REMARKS]?.trim() || undefined,
  };
};
